package ai.wagemix.web.components

import ai.wagemix.web.theme.WMBodySmallTextStyle
import ai.wagemix.web.theme.WMBodyTextStyle
import ai.wagemix.web.theme.WMControlCornerVar
import ai.wagemix.web.theme.WMControlHeightVar
import ai.wagemix.web.theme.WMFieldLabelGapVar
import ai.wagemix.web.theme.WMHairlineVar
import ai.wagemix.web.theme.WMIconSizeVar
import ai.wagemix.web.theme.WMInputPaddingInlineVar
import ai.wagemix.web.theme.WMLabelTextStyle
import ai.wagemix.web.theme.WMTightGapVar
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.fsryan.foundation.web.theme.fs
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.ariaLabel
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.border
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.cursor
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.height
import com.varabyte.kobweb.compose.ui.modifiers.id
import com.varabyte.kobweb.compose.ui.modifiers.left
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.modifiers.pointerEvents
import com.varabyte.kobweb.compose.ui.modifiers.position
import com.varabyte.kobweb.compose.ui.modifiers.right
import com.varabyte.kobweb.compose.ui.modifiers.setVariable
import com.varabyte.kobweb.compose.ui.modifiers.top
import com.varabyte.kobweb.compose.ui.modifiers.translateY
import com.varabyte.kobweb.compose.ui.thenIf
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.forms.InputKind
import com.varabyte.kobweb.silk.components.forms.InputStyle
import com.varabyte.kobweb.silk.components.forms.InputVars
import com.varabyte.kobweb.silk.components.forms.TextInput
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.components.forms.OutlinedInputVariant
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.CssStyleVariant
import com.varabyte.kobweb.silk.style.addVariant
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import com.varabyte.kobweb.silk.theme.colors.palette.toPalette
import org.jetbrains.compose.web.attributes.disabled
import org.jetbrains.compose.web.attributes.selected
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.Position
import com.varabyte.kobweb.compose.css.PointerEvents
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Label
import org.jetbrains.compose.web.dom.Option
import org.jetbrains.compose.web.dom.Select
import org.jetbrains.compose.web.dom.Text

/**
 * Penpot `Wagemix / Input`: 40px tall, 8px corner, `outline` border, surface fill. It refines Silk's outlined variant
 * ([WMTextField] applies both), which draws the border and its hover, focus and invalid colours; on its own this
 * variant only sets the colours, and the field had no visible edge.
 */
val WMInputVariant: CssStyleVariant<InputKind> = InputStyle.addVariant {
    base {
        val colors = colorMode.toPalette().fs
        Modifier
            .setVariable(InputVars.Height, WMControlHeightVar.value())
            .setVariable(InputVars.BorderRadius, WMControlCornerVar.value())
            .setVariable(InputVars.BorderColor, colors.outline)
            .setVariable(InputVars.FilledColor, colors.surface)
            .setVariable(InputVars.FilledHoverColor, colors.surface)
            .setVariable(InputVars.FilledFocusColor, colors.surface)
            .setVariable(InputVars.Padding, WMInputPaddingInlineVar.value())
            .height(WMControlHeightVar.value())
            .borderRadius(WMControlCornerVar.value())
            .backgroundColor(colors.surface)
            .color(colors.onSurface)
    }
}

/** Padding that leaves room for the password reveal button drawn over the input's right end. */
val WMInputWithTrailingButtonStyle = CssStyle.base {
    Modifier.padding(right = 40.px)
}

/** Padding that leaves room for a leading icon drawn over the input. */
val WMInputWithIconStyle = CssStyle.base {
    Modifier.setVariable(InputVars.Padding, 36.px).padding(left = 36.px)
}

/**
 * Penpot `Wagemix / Input` (Type: Text | Search | With icon). A Silk `TextInput` with the
 * Wagemix variant; [leadingIcon] is drawn inside the field's left padding.
 *
 * A [password] field masks its text and, when [showPasswordLabel] and [hidePasswordLabel] are given, has an eye
 * button at its right end that shows and hides the text; the labels name the button for assistive technology.
 */
@Composable
fun WMTextField(
    text: String,
    onTextChange: (String) -> Unit,
    modifier: Modifier = Modifier,
    placeholder: String? = null,
    accessibilityString: String? = placeholder,
    leadingIcon: WMIconGlyph? = null,
    enabled: Boolean = true,
    valid: Boolean = true,
    password: Boolean = false,
    showPasswordLabel: String? = null,
    hidePasswordLabel: String? = null,
    readOnly: Boolean = false,
    onCommit: () -> Unit = {}
) {
    val colors = ColorMode.current.toPalette().fs
    val revealable = password && showPasswordLabel != null && hidePasswordLabel != null
    var revealed by remember { mutableStateOf(false) }
    Box(modifier = Modifier.fillMaxWidth().position(Position.Relative).then(modifier)) {
        TextInput(
            text = text,
            onTextChange = onTextChange,
            modifier = Modifier.fillMaxWidth()
                .thenIf(leadingIcon != null) { WMInputWithIconStyle.toModifier() }
                .thenIf(revealable) { WMInputWithTrailingButtonStyle.toModifier() }
                .thenIf(accessibilityString != null) { Modifier.ariaLabel(accessibilityString!!) },
            variant = OutlinedInputVariant.then(WMInputVariant),
            placeholder = placeholder,
            focusBorderColor = colors.primary,
            invalidBorderColor = colors.error,
            password = password && !revealed,
            enabled = enabled,
            valid = valid,
            readOnly = readOnly,
            onCommit = onCommit
        )
        leadingIcon?.let { glyph ->
            Box(
                modifier = Modifier
                    .position(Position.Absolute)
                    .left(WMInputPaddingInlineVar.value())
                    .top(50.percent)
                    .translateY((-50).percent)
                    .color(colors.onSurfaceVariant)
                    .pointerEvents(PointerEvents.None)
            ) {
                WMIcon(glyph, accessibilityString = null, size = WMIconSizeVar.value())
            }
        }
        if (revealable) {
            WMIconButton(
                glyph = if (revealed) WMIconGlyph.EyeOff else WMIconGlyph.Eye,
                accessibilityString = if (revealed) hidePasswordLabel.orEmpty() else showPasswordLabel.orEmpty(),
                modifier = Modifier
                    .position(Position.Absolute)
                    .right(4.px)
                    .top(50.percent)
                    .translateY((-50).percent),
                size = WMButtonSize.Sm,
                enabled = enabled,
                onClick = { revealed = !revealed }
            )
        }
    }
}

val WMSelectStyle = CssStyle.base {
    val colors = colorMode.toPalette().fs
    Modifier
        .fillMaxWidth()
        .height(WMControlHeightVar.value())
        .padding(leftRight = WMInputPaddingInlineVar.value())
        .borderRadius(WMControlCornerVar.value())
        .border(WMHairlineVar.value(), LineStyle.Solid, colors.outline)
        .backgroundColor(colors.surface)
        .color(colors.onSurface)
        .cursor(Cursor.Pointer)
}

/** One choice of a [WMSelect]. */
data class WMSelectOption(val value: String, val label: String)

/**
 * Penpot `Wagemix / Input` (Type: Select): a native `<select>` in the Wagemix input frame.
 * Native selects stay keyboard- and screen-reader-friendly without extra work.
 */
@Composable
fun WMSelect(
    options: List<WMSelectOption>,
    selectedValue: String?,
    onSelect: (String) -> Unit,
    modifier: Modifier = Modifier,
    accessibilityString: String? = null,
    placeholder: String? = null,
    enabled: Boolean = true
) {
    Select(
        attrs = WMSelectStyle.toModifier().then(WMBodyTextStyle.toModifier()).then(modifier).toAttrs {
            if (!enabled) disabled()
            accessibilityString?.let { attr("aria-label", it) }
            onChange { event -> event.value?.let(onSelect) }
        }
    ) {
        if (placeholder != null) {
            Option(value = "", attrs = { if (selectedValue == null) selected(); disabled() }) { Text(placeholder) }
        }
        options.forEach { option ->
            Option(value = option.value, attrs = { if (option.value == selectedValue) selected() }) { Text(option.label) }
        }
    }
}

/**
 * Penpot `Wagemix / Field`: a label above a control with helper or error text below it.
 * [controlId] links the label to the control for assistive technology.
 */
@Composable
fun WMField(
    label: String,
    modifier: Modifier = Modifier,
    controlId: String? = null,
    helperText: String? = null,
    errorText: String? = null,
    control: @Composable () -> Unit
) {
    val colors = ColorMode.current.toPalette().fs
    Column(modifier = Modifier.fillMaxWidth().gap(WMFieldLabelGapVar.value()).then(modifier)) {
        Label(forId = controlId, attrs = WMLabelTextStyle.toModifier().color(colors.onSurface).toAttrs()) { Text(label) }
        control()
        when {
            errorText != null -> SpanText(errorText, WMBodySmallTextStyle.toModifier().color(colors.error))
            helperText != null -> SpanText(helperText, WMBodySmallTextStyle.toModifier().color(colors.onSurfaceVariant))
        }
    }
}

/** A row of controls that share the width equally (the low/high pay-range pair). */
@Composable
fun WMFieldRow(modifier: Modifier = Modifier, content: @Composable () -> Unit) {
    Row(
        modifier = Modifier.fillMaxWidth().gap(WMTightGapVar.value()).then(modifier),
        verticalAlignment = Alignment.CenterVertically
    ) {
        content()
    }
}

/** Gives a control a stable DOM id so a [WMField] label can point at it. */
fun Modifier.wmControlId(id: String): Modifier = this.id(id)
